import React from 'react';
import { styled } from '@compiled/react';
import ProgressBar from '@atlaskit/progress-bar';
import { token } from '@atlaskit/tokens';
import { Tokens } from '@atlassian/jira-custom-theme-constants';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	type MigrationId,
	type MigrationStatus,
	Status,
} from '@atlassian/jira-project-create-importer-common';
import { EXPERIENCES } from '../../common/constants/metrics';
import { useTrackMigrationStatus } from '../../common/utils/use-track-migration-status';
import messages from './messages';

type Props = {
	migrationId: MigrationId;
	onSuccess: (migrationStatus: MigrationStatus) => void;
	onFail: (migrationStatus: MigrationStatus) => void;
};

const formatProgress = (progress: number) => `${(progress * 100).toFixed(0)}%`;

export const ImportProgressBar = ({ migrationId, onSuccess, onFail }: Props) => {
	const { formatMessage } = useIntl();
	const { migrationStatus } = useTrackMigrationStatus(
		EXPERIENCES.IMPORT_PART_2,
		migrationId,
		onSuccess,
		onFail,
	);

	if (fg('enable-x2c-standardised-flow')) {
		return null;
	}

	if (migrationStatus.overallStatus === Status.FAILED) {
		return null;
	}

	if (migrationStatus.overallStatus === Status.SUCCESS) {
		return (
			<Wrapper>
				<HeadingWrapper>
					<HeadingBold>{formatMessage(messages.importComplete)}</HeadingBold>
				</HeadingWrapper>
				<ProgressBar
					value={1}
					appearance="success"
					testId="project-create-import-progress.ui.import-progress-bar.success-progress-bar"
				/>
			</Wrapper>
		);
	}

	return (
		<Wrapper>
			<HeadingWrapper>
				<Percentage data-testid="project-create-import-progress.ui.import-progress-bar.percentage">
					{formatProgress(migrationStatus.overallProgress)}
				</Percentage>
				<Heading>{formatMessage(messages.importInProgress)}</Heading>
			</HeadingWrapper>
			<ProgressBar
				value={migrationStatus.overallProgress}
				appearance="success"
				testId="project-create-import-progress.ui.import-progress-bar.in-progress-bar"
			/>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	maxWidth: '50%',
	width: '400px',
	margin: `0 ${token('space.100', '8px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: `${Tokens.COLOR_TEXT}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Percentage = styled.span({
	fontWeight: 700,
	marginRight: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/design-system/no-empty-styled-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Heading = styled.span({});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingBold = styled.span({
	fontWeight: 700,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	font: token('font.body.small'),
	margin: `${token('space.025', '2px')} 0px`,
});
